import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type { Expense } from '../../../types/bookkeeping.js';

const STATUS_CLASSES: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  void: 'bg-slate-100 text-slate-500',
};

export function Expenses() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['bk-expenses', activeOrgId],
    queryFn: () => api<{ expenses: Expense[] }>('/bookkeeping/purchases/expenses?limit=50'),
    enabled: !!activeOrgId,
  });

  const expenses = data?.expenses ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('purchases.expenses.title')}</h1>
        <Link
          to="/app/bookkeeping/purchases/expenses/new"
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          + {t('purchases.expenses.new')}
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('purchases.expenses.fields.number')}</th>
                <th className="px-3 py-2">{t('purchases.expenses.fields.supplier_number')}</th>
                <th className="px-3 py-2">{t('purchases.expenses.fields.vendor')}</th>
                <th className="px-3 py-2">{t('purchases.expenses.fields.entry_date')}</th>
                <th className="px-3 py-2">{t('purchases.expenses.fields.currency')}</th>
                <th className="px-3 py-2 text-right">
                  {t('purchases.expenses.fields.grand_total')}
                </th>
                <th className="px-3 py-2">{t('purchases.expenses.fields.payment_account')}</th>
                <th className="px-3 py-2">{t('purchases.expenses.fields.status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-4 text-center text-slate-400">
                    {t('purchases.expenses.empty', { defaultValue: 'No hay gastos aún.' })}
                  </td>
                </tr>
              )}
              {expenses.map((exp) => (
                <tr key={exp.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link
                      to={`/app/bookkeeping/purchases/expenses/${exp.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {exp.number}
                    </Link>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{exp.supplier_number ?? '—'}</td>
                  <td className="px-3 py-2">{exp.vendor_id ?? '—'}</td>
                  <td className="px-3 py-2">{exp.entry_date}</td>
                  <td className="px-3 py-2">{exp.currency}</td>
                  <td className="px-3 py-2 text-right font-mono">{exp.grand_total}</td>
                  <td className="px-3 py-2 font-mono text-xs">{exp.payment_account_id}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-sm px-1.5 py-0.5 text-xs ${STATUS_CLASSES[exp.status] ?? ''}`}
                    >
                      {t(`purchases.expenses.statuses.${exp.status}`, { defaultValue: exp.status })}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/app/bookkeeping/purchases/expenses/${exp.id}`}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {t('journalEntries.viewEdit')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
